// The person's own settings on this device: theme, light or dark, motion, sound, haptics.
// Kept in localStorage under one key. index.html reads the same key before the first paint.
// Checked by hand (five fields) rather than with Zod, so the first screen doesn't carry a schema library.
import { useSyncExternalStore } from "react";

export type Settings = {
  theme: "plain" | "dialup" | "backstage" | "arcade";
  mode: "light" | "dark" | "system";
  motion: "system" | "reduce";
  sound: boolean;
  haptics: boolean;
  /** Arcade's Focus mode: no glow, no grid, no overshoot, no sound. */
  focus: boolean;
};
export const DEFAULT_SETTINGS: Settings = { theme: "plain", mode: "system", motion: "system", sound: false, haptics: false, focus: false };
const KEY = "sr2-settings";

const oneOf = <T extends string>(v: unknown, allowed: readonly T[], fallback: T): T => (allowed.includes(v as T) ? (v as T) : fallback);
const bool = (v: unknown, fallback: boolean) => (typeof v === "boolean" ? v : fallback);

/** Anything odd in storage falls back to the default for that field. */
export function parseSettings(raw: unknown): Settings {
  const o = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  return {
    theme: oneOf(o.theme, ["plain", "dialup", "backstage", "arcade"] as const, DEFAULT_SETTINGS.theme),
    mode: oneOf(o.mode, ["light", "dark", "system"] as const, DEFAULT_SETTINGS.mode),
    motion: oneOf(o.motion, ["system", "reduce"] as const, DEFAULT_SETTINGS.motion),
    sound: bool(o.sound, DEFAULT_SETTINGS.sound),
    haptics: bool(o.haptics, DEFAULT_SETTINGS.haptics),
    focus: bool(o.focus, DEFAULT_SETTINGS.focus),
  };
}

function read(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? parseSettings(JSON.parse(raw)) : DEFAULT_SETTINGS;
  } catch {
    return DEFAULT_SETTINGS;
  }
}

let current: Settings = read();
const listeners = new Set<() => void>();

export const settings = {
  get: (): Settings => current,
  update: (patch: Partial<Settings>): void => {
    current = { ...current, ...patch };
    try {
      localStorage.setItem(KEY, JSON.stringify(current));
    } catch {
      // storage can be full or blocked; the setting still applies for this visit
    }
    for (const l of listeners) l();
  },
  subscribe: (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

export function useSettings(): Settings {
  return useSyncExternalStore(settings.subscribe, settings.get, settings.get);
}

export function resetSettingsForTests() {
  current = read();
}
